import { fromUnixTime, getUnixTime } from "date-fns/esm";
import { formatInTimeZone } from "date-fns-tz";
import groupBy from "lodash-es/groupBy";

import { Datum } from "ds/components/Charts/BarChart/Chart";
import { RunMinutesUsage, RunMinutesUsageInput } from "types/generated";
import { uniqByKey } from "utils/uniq";
import { formatUsageDateRange } from "views/Account/Settings/Usage/utils";

import { RunMinutesUsageData, RunMinutesUsageSeriesPoint } from "./types";

const SECONDS_PER_DAY = 86400;
const WINDOW_DAYS = 31;

// The API buckets by UTC day, so the local calendar never enters the arithmetic:
// a local midnight can be a DST hour off UTC midnight, or skipped altogether.
const TIME_ZONE = "UTC";

export const formatRunMinutesUsageDate = (unixSeconds: number, pattern: string) =>
  formatInTimeZone(unixSeconds * 1000, TIME_ZONE, pattern);

export const getRunMinutesUsageWindow = (now: Date = new Date()): RunMinutesUsageInput => {
  const endTime = Math.floor(getUnixTime(now) / SECONDS_PER_DAY) * SECONDS_PER_DAY;

  return { startTime: endTime - WINDOW_DAYS * SECONDS_PER_DAY, endTime };
};

export const getRunMinutesUsageWindowCaption = ({
  windowStart,
  windowEnd,
}: Pick<RunMinutesUsageData, "windowStart" | "windowEnd">) => {
  const days = (getUnixTime(windowEnd) - getUnixTime(windowStart)) / SECONDS_PER_DAY + 1;

  return `Last ${days} days: ${formatUsageDateRange(windowStart, windowEnd)}`;
};

type RunMinutesUsagePayload = Pick<
  RunMinutesUsage,
  "window" | "dailyByRunType" | "dailyByRunState" | "totals"
>;

export const normalizeRunMinutesUsage = (usage: RunMinutesUsagePayload): RunMinutesUsageData => ({
  windowStart: fromUnixTime(usage.window.start),
  windowEnd: fromUnixTime(usage.window.end - SECONDS_PER_DAY),
  byRunType: usage.dailyByRunType.map(({ day, runType, minutes }) => ({
    day,
    key: runType,
    minutes,
  })),
  byRunState: usage.dailyByRunState.map(({ day, runState, minutes }) => ({
    day,
    key: runState,
    minutes,
  })),
  privateMinutes: usage.totals.privateMinutes,
  publicMinutes: usage.totals.publicMinutes,
});

export const getRunMinutesUsageChartItems = (series: RunMinutesUsageSeriesPoint[]) =>
  uniqByKey(
    [...series].sort((a, b) => a.day - b.day),
    "key"
  ).map(({ key }) => key);

// A stacked bar reads every item on every row, so days that miss one get a zero.
export const adaptRunMinutesUsageToChart = (
  series: RunMinutesUsageSeriesPoint[],
  items: string[]
): Datum[] =>
  Object.entries(groupBy(series, "day"))
    .map(([day, points]) => ({
      timestamp: Number(day),
      ...items.reduce<Record<string, number>>((acc, item) => {
        acc[item] = points.find(({ key }) => key === item)?.minutes || 0;
        return acc;
      }, {}),
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
